import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PropTypes } from 'prop-types';
import { withStyles } from '@mui/styles';
import {
  Button,
  Card,
  CardContent,
  ButtonBase,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  FormControl,
  Icon,
  IconButton,
  InputLabel,
  LinearProgress,
  ListItemText,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { StoreUtil, SpecialtyUtil } from 'doctivity-shared/utils';
import { loadAnalytics } from 'store/actions/analyticsActions';
import {
  ServiceLineSelect,
  LoadingView,
  Paginateable,
  DynamicTable,
} from 'components';
import { ColorUtil } from 'utils';
import axiosInstance from 'utils/axiosUtil';
import fileDownload from 'js-file-download';
import { format } from 'date-fns';

// CSV export always pulls up to this many provider rows, regardless of the
// page size currently shown in the UI.
const CSV_MAX_ROWS = 2000;

const styles = (theme) => ({
  card: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  titleRow: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
  titleLabelName: {
    color: theme.palette.secondary.main,
    textDecoration: 'underline',
  },
  titleSpacer: {
    flex: 1,
  },
  exportIcon: {
    fontSize: 16,
    fontWeight: 200,
  },
  filters: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  claimsDateDropDown: {
    width: 160,
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  collapseDropDown: {
    width: 200,
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  filterLabel: {
    backgroundColor: 'white',
    paddingRight: 4,
  },
  select: {
    '& .MuiMenuItem-root.Mui-selected, .MuiMenuItem-root.Mui-selected:hover': {
      backgroundColor: '#00a887',
      color: 'white',
    },
  },
  // Multiselect menu: keep selected rows visually unchanged (only the checkbox
  // reflects selection).
  collapseMenu: {
    '& .MuiMenuItem-root.Mui-selected': {
      backgroundColor: 'transparent',
    },
  },
  notLabelledItem: {
    fontStyle: 'italic',
  },
  rollUpLabelLink: {
    cursor: 'pointer',
    '&:hover': { textDecoration: 'underline' },
  },
  // List (mirrors the market-data provider list)
  tableContainer: {
    width: '100%',
    fontSize: 12,
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#EBF1F8',
    borderRadius: '4px 4px 0 0',
    padding: '8px 0',
    marginBottom: 2,
    width: '100%',
  },
  headerCell: {
    color: '#64748B',
    fontSize: 10,
    fontWeight: 600,
    textTransform: 'uppercase',
    padding: '0 8px',
    whiteSpace: 'nowrap',
  },
  dataRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '6px 0',
    borderRadius: 2,
    cursor: 'pointer',
    width: '100%',
    textAlign: 'left',
    '&:hover': {
      backgroundColor: '#F8FAFC',
    },
    '&:nth-of-type(even)': {
      backgroundColor: '#F8FAFC',
    },
  },
  cell: {
    padding: '0 8px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  iconCol: {
    width: '2%',
    minWidth: 28,
    display: 'flex',
    justifyContent: 'center',
  },
  nameCol: {
    width: '20%',
    minWidth: 120,
  },
  specialtyCol: {
    width: '15%',
    minWidth: 80,
  },
  orgCol: {
    width: '20%',
    minWidth: 100,
  },
  labelCol: {
    width: '12%',
    minWidth: 80,
  },
  patientsCol: {
    width: 20,
    minWidth: 20,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    paddingRight: 0,
  },
  pctCol: {
    width: 48,
    minWidth: 48,
    flexShrink: 0,
    textAlign: 'left',
    paddingLeft: 8,
    fontSize: 11,
    fontWeight: 400,
    color: '#4B5563',
  },
  chartCol: {
    flex: 1,
    minWidth: 150,
    paddingRight: 12,
  },
  // Spans both the patient-icon column (110px) and the bar column so the
  // "Shared Patients" header sits above both.
  sharedHeaderCol: {
    flex: 1,
    minWidth: 178,
  },
  providerName: {
    fontWeight: 500,
    color: '#212529',
    fontSize: 12,
    textTransform: 'uppercase',
  },
  collapsedRow: {
    backgroundColor: '#EEF6F4',
    cursor: 'pointer',
    '&:hover': { backgroundColor: '#E3F0EC' },
    '&:nth-of-type(even)': { backgroundColor: '#EEF6F4' },
  },
  collapsedName: {
    fontWeight: 700,
    color: '#0B7A63',
    fontSize: 12,
    textTransform: 'uppercase',
  },
  collapsedIcon: {
    fontSize: 16,
    color: '#00a887',
  },
  specialty: {
    color: '#4B5563',
    fontSize: 11,
    fontWeight: 300,
  },
  orgName: {
    color: '#4B5563',
    fontSize: 11,
    fontWeight: 300,
  },
  affiliatedIcon: {
    fontSize: 14,
    color: '#5391D5',
  },
  patientIcon: {
    fontSize: 12,
    color: '#9CA3AF',
    marginRight: 4,
  },
  patientCount: {
    fontSize: 11,
    fontWeight: 400,
    color: '#4B5563',
  },
  barContainer: {
    width: '100%',
    height: 18,
    backgroundColor: 'transparent',
    borderRadius: 2,
    overflow: 'hidden',
  },
  bar: {
    height: '100%',
    borderRadius: 2,
    transition: 'width 0.5s ease-in-out',
  },
  empty: {
    padding: theme.spacing(4),
    textAlign: 'center',
    color: '#64748B',
    fontSize: 14,
    fontWeight: 300,
  },
  processing: {
    padding: theme.spacing(5),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  processingText: {
    textAlign: 'center',
    color: '#64748B',
    fontSize: 14,
    fontWeight: 300,
    maxWidth: 420,
  },
  progressBarWrap: {
    width: '100%',
    maxWidth: 420,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  progressBar: {
    flex: 1,
    height: 8,
    borderRadius: 4,
  },
  progressPct: {
    fontSize: 13,
    fontWeight: 500,
    color: '#64748B',
    minWidth: 36,
    textAlign: 'right',
  },
  infoTip: {
    fontSize: 13,
    fontWeight: 200,
  },
  // Hover popup listing connected providers within the label
  tipPaper: {
    maxWidth: 'none',
    backgroundColor: '#fff',
    color: '#212529',
    border: '1px solid #E2E8F0',
    boxShadow: '0 6px 20px rgba(15, 23, 42, 0.18)',
    padding: 0,
  },
  tipTable: {
    fontSize: 11,
    minWidth: 360,
    maxWidth: 480,
  },
  tipTitle: {
    padding: '10px 12px 6px',
    fontSize: 14,
    fontWeight: 700,
    color: '#0B7A63',
  },
  tipHeaderRow: {
    display: 'flex',
    borderBottom: '1px solid #E2E8F0',
    padding: '4px 10px',
    fontSize: 10,
    fontWeight: 600,
    color: '#64748B',
    textTransform: 'uppercase',
  },
  tipRow: {
    display: 'flex',
    padding: '4px 10px',
    '&:nth-of-type(even)': { backgroundColor: '#F8FAFC' },
  },
  tipName: {
    width: '40%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    paddingRight: 6,
    textTransform: 'uppercase',
  },
  tipOrg: {
    width: '42%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    paddingRight: 6,
    color: '#4B5563',
  },
  tipCount: {
    width: '18%',
    textAlign: 'right',
  },
  tipMessage: {
    padding: '10px',
    color: '#64748B',
    fontSize: 11,
  },
  dialogTitle: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
  },
  dialogTitleText: {
    fontSize: 18,
    fontWeight: 700,
    color: theme.palette.primary.main,
    lineHeight: 1.3,
  },
  dialogLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    marginBottom: 12,
    fontFamily: theme.typography.fontFamily,
    fontSize: 13,
    fontWeight: 600,
    color: '#5391D5',
    textDecoration: 'none',
    '&:hover': { textDecoration: 'underline' },
  },
  // Providers icon (same as the left nav) beside the "Go To" link.
  dialogLinkIcon: {
    fontSize: 16,
  },
  titleProviderLink: {
    color: theme.palette.secondary.main,
    textDecoration: 'underline',
    '&:hover': { opacity: 0.8 },
  },
  // Key's label suggestion shown in the dialog for unlabelled providers.
  suggestBox: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    marginBottom: 12,
    padding: '8px 10px',
    backgroundColor: '#F4F9F7',
    border: '1px solid #DCEBE5',
    borderRadius: 4,
    fontFamily: theme.typography.fontFamily,
    fontSize: 12,
    fontWeight: 300,
    color: '#334155',
    lineHeight: 1.5,
  },
  suggestIcon: {
    fontSize: 18,
    color: '#00a887',
    marginTop: 1,
    flexShrink: 0,
  },
  suggestLabelName: {
    fontWeight: 600,
    color: '#0B7A63',
  },
  suggestApplyButton: {
    marginLeft: 8,
    padding: '0 8px',
    fontSize: 11,
    fontWeight: 600,
    lineHeight: 1.8,
    textTransform: 'none',
    flexShrink: 0,
    alignSelf: 'center',
  },
  suggestApplied: {
    fontWeight: 600,
    color: '#0B7A63',
    whiteSpace: 'nowrap',
    alignSelf: 'center',
  },
});

// Click detail dialog: providers in the current label that share patients with
// the clicked provider, in the opposite direction from the page. Fetched
// lazily when the dialog opens and cached per provider/direction/serviceline.
const DIALOG_COLUMNS = [
  { label: 'Provider', key: 'name', sortable: false },
  {
    label: 'Organization',
    key: 'organization',
    sortable: false,
    format: (v) => v || '-',
  },
  {
    label: 'Shared Patients',
    key: 'shared_patients',
    sortable: false,
    format: (v) => (v || 0).toLocaleString(),
  },
];

function ProviderSharedDialog(props) {
  const {
    classes,
    open,
    onClose,
    info,
    query,
    onQueryChange,
    onSuggestionApplied,
  } = props;
  const dispatch = useDispatch();
  const storeId = info ? info.storeId : null;
  const result = useSelector((state) =>
    storeId ? StoreUtil.get(state.analytics, storeId) : null,
  );

  useEffect(() => {
    if (open && info && storeId) {
      dispatch(
        loadAnalytics({
          type: 'LABEL_PROVIDER_SHARED',
          store_id: storeId,
          filter: info.filter,
          opts: { limit: query.limit, offset: query.offset },
        }),
      );
    }
  }, [open, storeId, query.limit, query.offset, dispatch]);

  // Key's label suggestion for unlabelled providers. Fetched when the dialog
  // opens; cached per label+provider so reopening doesn't re-ask (the answer
  // is slow — Key may web-search the provider).
  const [suggestion, setSuggestion] = useState(null);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [applyState, setApplyState] = useState(null); // null | 'applying' | 'error'
  const [similarState, setSimilarState] = useState(null); // null | 'running' | 'error'
  const suggestCacheRef = useRef(new Map());
  const notLabelled = !!info && info.providerLabelId == null;
  const suggestTagId = info?.filter?.tag_id;
  const suggestProviderId = info?.providerId;

  useEffect(() => {
    if (!open || !notLabelled || !suggestTagId || !suggestProviderId) {
      return undefined;
    }
    setApplyState(null);
    setSimilarState(null);
    const key = `${suggestTagId}:${suggestProviderId}`;
    const cached = suggestCacheRef.current.get(key);
    if (cached) {
      setSuggestion(cached);
      setSuggestLoading(false);
      return undefined;
    }
    let cancelled = false;
    setSuggestion(null);
    setSuggestLoading(true);
    axiosInstance
      .get(`/v2/labels/${suggestTagId}/suggest/${suggestProviderId}`)
      .then((response) => {
        if (cancelled) {
          return;
        }
        suggestCacheRef.current.set(key, response.data);
        setSuggestion(response.data);
      })
      .catch((error) => {
        console.error('Error getting label suggestion:', error);
        if (!cancelled) {
          setSuggestion({ error: true });
        }
      })
      .finally(() => {
        if (!cancelled) {
          setSuggestLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, notLabelled, suggestTagId, suggestProviderId]);

  // Apply Key's suggestion: create the ProviderTag membership (this flows
  // through the incremental label-totals delta on the backend, so the Patient
  // Flow cache catches up within seconds).
  const onApplySuggestion = useCallback(async () => {
    if (!suggestion || suggestion.label_id == null || !suggestProviderId) {
      return;
    }
    setApplyState('applying');
    try {
      await axiosInstance.put(
        '/ProviderTags',
        [{ tag_id: suggestion.label_id, provider_id: suggestProviderId }],
        { params: { opts: { include: [] } } },
      );
      const updated = { ...suggestion, applied: true };
      suggestCacheRef.current.set(
        `${suggestTagId}:${suggestProviderId}`,
        updated,
      );
      setSuggestion(updated);
      setApplyState(null);
      if (onSuggestionApplied) {
        onSuggestionApplied();
      }
    } catch (error) {
      console.error('Error applying suggested label:', error);
      setApplyState('error');
    }
  }, [suggestion, suggestTagId, suggestProviderId, onSuggestionApplied]);

  // Create the NEW label Key proposed (in this label type) and put the
  // provider in it.
  const onCreateSuggestedLabel = useCallback(async () => {
    if (!suggestion || !suggestion.new_label || !suggestProviderId) {
      return;
    }
    setApplyState('applying');
    try {
      const response = await axiosInstance.post(
        `/v2/labels/${suggestTagId}/suggest/${suggestProviderId}/create`,
        { name: suggestion.new_label },
      );
      const updated = {
        ...suggestion,
        applied: true,
        created_label: response.data,
      };
      suggestCacheRef.current.set(
        `${suggestTagId}:${suggestProviderId}`,
        updated,
      );
      setSuggestion(updated);
      setApplyState(null);
      if (onSuggestionApplied) {
        onSuggestionApplied();
      }
    } catch (error) {
      console.error('Error creating suggested label:', error);
      setApplyState('error');
    }
  }, [suggestion, suggestTagId, suggestProviderId, onSuggestionApplied]);

  // "Label Similar Providers": ask Key to review the top unlabelled
  // providers of this patient flow and label the ones that clearly belong in
  // the label the reference provider was just given.
  const onLabelSimilar = useCallback(async () => {
    const targetLabelId =
      suggestion?.created_label?.tag_id ?? suggestion?.label_id;
    if (!targetLabelId || !suggestTagId || !suggestProviderId) {
      return;
    }
    setSimilarState('running');
    try {
      const response = await axiosInstance.post(
        `/v2/labels/${suggestTagId}/suggest/${suggestProviderId}/similar`,
        {
          target_label_id: targetLabelId,
          direction: info?.filter?.direction,
          serviceline: info?.filter?.serviceline,
        },
      );
      const updated = { ...suggestion, similar_result: response.data };
      suggestCacheRef.current.set(
        `${suggestTagId}:${suggestProviderId}`,
        updated,
      );
      setSuggestion(updated);
      setSimilarState(null);
      if (onSuggestionApplied) {
        onSuggestionApplied();
      }
    } catch (error) {
      console.error('Error labeling similar providers:', error);
      setSimilarState('error');
    }
  }, [
    suggestion,
    suggestTagId,
    suggestProviderId,
    info?.filter?.direction,
    info?.filter?.serviceline,
    onSuggestionApplied,
  ]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle className={classes.dialogTitle}>
        <span className={classes.dialogTitleText}>
          {info && (
            <>
              Providers in{' '}
              <span className={classes.titleLabelName}>{info.labelName}</span>{' '}
              that see patients {info.oppositeLabel}{' '}
              <a
                className={classes.titleProviderLink}
                href={`/providers/${info.providerId}/market`}
              >
                {info.providerName}
              </a>
            </>
          )}
        </span>
        <IconButton onClick={onClose} size='small' aria-label='close'>
          <Icon>close</Icon>
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {info && (
          <a
            className={classes.dialogLink}
            href={`/providers/${info.providerId}/market`}
          >
            <Icon className={classes.dialogLinkIcon}>people</Icon>
            Go To {info.providerName} Market Data
          </a>
        )}
        {notLabelled && (suggestLoading || (suggestion && !suggestion.error)) && (
          <div className={classes.suggestBox}>
            <SupportAgentIcon className={classes.suggestIcon} />
            {suggestLoading && (
              <>
                <span>
                  This provider is not labelled — Key is looking for a label
                  suggestion…
                </span>
                <CircularProgress size={14} thickness={5} />
              </>
            )}
            {!suggestLoading && suggestion && suggestion.label_id != null && (
              <>
                <span>
                  Key suggests:{' '}
                  <span className={classes.suggestLabelName}>
                    {suggestion.label_name}
                  </span>
                  {suggestion.confidence
                    ? ` (${suggestion.confidence} confidence)`
                    : ''}
                  {suggestion.reasoning ? ` — ${suggestion.reasoning}` : ''}
                  {applyState === 'error' ? ' (setting the label failed, please try again)' : ''}
                </span>
                {suggestion.applied ? (
                  <span className={classes.suggestApplied}>Label set ✓</span>
                ) : (
                  <Button
                    variant='outlined'
                    size='small'
                    color='secondary'
                    className={classes.suggestApplyButton}
                    disabled={applyState === 'applying'}
                    onClick={onApplySuggestion}
                  >
                    {applyState === 'applying' ? 'Setting…' : 'Set Label'}
                  </Button>
                )}
              </>
            )}
            {!suggestLoading &&
              suggestion &&
              suggestion.label_id == null &&
              suggestion.new_label && (
                <>
                  <span>
                    Key suggests creating a new label:{' '}
                    <span className={classes.suggestLabelName}>
                      {suggestion.new_label}
                    </span>
                    {suggestion.confidence
                      ? ` (${suggestion.confidence} confidence)`
                      : ''}
                    {suggestion.reasoning ? ` — ${suggestion.reasoning}` : ''}
                    {applyState === 'error'
                      ? ' (creating the label failed, please try again)'
                      : ''}
                  </span>
                  {suggestion.applied ? (
                    <span className={classes.suggestApplied}>
                      Label created ✓
                    </span>
                  ) : (
                    <Button
                      variant='outlined'
                      size='small'
                      color='secondary'
                      className={classes.suggestApplyButton}
                      disabled={applyState === 'applying'}
                      onClick={onCreateSuggestedLabel}
                    >
                      {applyState === 'applying'
                        ? 'Creating…'
                        : 'Create New Label'}
                    </Button>
                  )}
                </>
              )}
            {!suggestLoading &&
              suggestion &&
              suggestion.label_id == null &&
              !suggestion.new_label && (
                <span>
                  Key couldn&apos;t identify a label that fits
                  {suggestion.reasoning
                    ? ` — ${suggestion.reasoning}`
                    : ' this provider.'}
                </span>
              )}
          </div>
        )}
        {notLabelled && suggestion?.applied && (
          <div className={classes.suggestBox}>
            <SupportAgentIcon className={classes.suggestIcon} />
            {!suggestion.similar_result && similarState !== 'running' && (
              <>
                <span>
                  Key: Would you like me to review and label other similar
                  providers?
                  {similarState === 'error'
                    ? ' (the review failed, please try again)'
                    : ''}
                </span>
                <Button
                  variant='outlined'
                  size='small'
                  color='secondary'
                  className={classes.suggestApplyButton}
                  onClick={onLabelSimilar}
                >
                  Label Similar Providers
                </Button>
              </>
            )}
            {similarState === 'running' && (
              <>
                <span>
                  Key is reviewing the top unlabelled providers in this patient
                  flow…
                </span>
                <CircularProgress size={14} thickness={5} />
              </>
            )}
            {similarState !== 'running' && suggestion.similar_result && (
              <span>
                Key reviewed {suggestion.similar_result.reviewed} unlabelled
                provider
                {suggestion.similar_result.reviewed === 1 ? '' : 's'} and
                labeled {suggestion.similar_result.count || 'none'}
                {suggestion.similar_result.count > 0 ? (
                  <>
                    {' as '}
                    <span className={classes.suggestLabelName}>
                      {suggestion.similar_result.label_name}
                    </span>
                    {suggestion.similar_result.labeled?.length > 0
                      ? `: ${suggestion.similar_result.labeled
                          .slice(0, 5)
                          .map((p) => p.name)
                          .join('; ')}${
                          suggestion.similar_result.labeled.length > 5
                            ? '…'
                            : ''
                        }`
                      : ''}
                  </>
                ) : (
                  ' — no candidates met the high-confidence bar.'
                )}
              </span>
            )}
          </div>
        )}
        <DynamicTable
          columns={DIALOG_COLUMNS}
          data={result}
          query={query}
          onQueryChange={onQueryChange}
          idKey='provider_id'
          getRowHref={(row) => `/providers/${row.provider_id}/market`}
          ContainerType={React.Fragment}
          showFilters={false}
        />
      </DialogContent>
    </Dialog>
  );
}

ProviderSharedDialog.propTypes = {
  classes: PropTypes.object,
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  info: PropTypes.object,
  query: PropTypes.object.isRequired,
  onQueryChange: PropTypes.func.isRequired,
  onSuggestionApplied: PropTypes.func,
};

function LabelPatientFlow(props) {
  const { tag, classes } = props;
  const dispatch = useDispatch();

  const selectedClient = useSelector((state) => state.app.selectedClient);
  const flow = useSelector((state) =>
    StoreUtil.get(state.analytics, 'LABEL_PATIENT_FLOW'),
  );

  const [direction, setDirection] = useState('after');
  const [serviceline, setServiceline] = useState('all');
  // Labels (by id) to collapse into a single summary row. Defaults to ALL
  // labels in the group (plus "Not Labelled") rolled up. Until the user
  // touches the Roll Up control, the request sends the 'all' sentinel —
  // the group's label ids aren't known before the first response.
  const [collapse, setCollapse] = useState([]);
  const [rollUpTouched, setRollUpTouched] = useState(false);
  const [limit, setLimit] = useState(100);
  const [offset, setOffset] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogInfo, setDialogInfo] = useState(null);
  const [dialogQuery, setDialogQuery] = useState({ limit: 10, offset: 0 });

  const closeDialog = useCallback(() => setDialogOpen(false), []);

  // Switching labels does NOT remount this component (TagSelector swaps the
  // tag prop in place), so reset the Roll Up selection and paging when the
  // tag changes. Otherwise collapse keeps ids from the previous label's
  // namespace, which the new response's labels list can't resolve to names.
  const prevTagIdRef = useRef(tag?.id);
  useEffect(() => {
    if (prevTagIdRef.current !== tag?.id) {
      prevTagIdRef.current = tag?.id;
      setCollapse([]);
      setRollUpTouched(false);
      setOffset(0);
    }
  }, [tag?.id]);

  const fetchFlow = useCallback(() => {
    dispatch(
      loadAnalytics({
        type: 'LABEL_PATIENT_FLOW',
        filter: {
          client_id: selectedClient,
          tag_id: tag.id,
          direction,
          serviceline,
          collapse: rollUpTouched ? collapse : 'all',
        },
        opts: {
          limit,
          offset,
        },
      }),
    );
  }, [
    tag.id,
    direction,
    serviceline,
    collapse,
    rollUpTouched,
    limit,
    offset,
    selectedClient,
    dispatch,
  ]);

  useEffect(() => {
    fetchFlow();
  }, [fetchFlow]);

  // After Key's suggestion is applied, the Patient Flow cache updates
  // asynchronously (incremental delta lambda) — give it a moment, then
  // refresh so the provider's new label shows in the list.
  const refreshTimerRef = useRef(null);
  const onSuggestionApplied = useCallback(() => {
    window.clearTimeout(refreshTimerRef.current);
    refreshTimerRef.current = window.setTimeout(fetchFlow, 5000);
  }, [fetchFlow]);
  useEffect(() => () => window.clearTimeout(refreshTimerRef.current), []);

  // Filter changes reset to the first page.
  const onDirectionChange = useCallback((event) => {
    setDirection(event.target.value);
    setOffset(0);
  }, []);

  const onServicelineChange = useCallback((id) => {
    setServiceline(id);
    setOffset(0);
  }, []);

  const onCollapseChange = useCallback((event) => {
    const value = event.target.value;
    setRollUpTouched(true);
    setCollapse(
      typeof value === 'string' ? value.split(',').map(Number) : value,
    );
    setOffset(0);
  }, []);

  const onQueryChange = useCallback((query) => {
    setLimit(query.limit);
    setOffset(query.offset);
  }, []);

  // CSV export. Refetches with the current filters (rather than reading the
  // redux store) so the file contains up to CSV_MAX_ROWS provider rows even
  // when the UI is paging through fewer. Headers mirror the on-screen column
  // names, not database column names.
  const onDownload = useCallback(async () => {
    try {
      const response = await axiosInstance.post('/Analytics', {
        type: 'LABEL_PATIENT_FLOW',
        filter: {
          client_id: selectedClient,
          tag_id: tag.id,
          direction,
          serviceline,
          collapse: rollUpTouched ? collapse : 'all',
        },
        opts: {
          limit: CSV_MAX_ROWS,
          offset: 0,
        },
      });

      const result = response.data || {};
      const csvCollapsed = result.collapsed ?? [];
      const csvRows = result.rows ?? [];
      const csvTotal = parseInt(result.totalShared, 10) || 0;

      // Match the on-screen percent formatting: rolled-up rows are coarser,
      // provider rows show 2 decimals.
      const formatPct = (shared, isCollapsed) => {
        if (csvTotal <= 0) {
          return '';
        }
        const pct = (shared / csvTotal) * 100;
        if (isCollapsed) {
          return `${pct >= 10 ? pct.toFixed(0) : pct.toFixed(1)}%`;
        }
        return `${pct.toFixed(2)}%`;
      };

      const headers = [
        'Affiliated',
        'Label',
        'Provider',
        'Specialty',
        'Organization',
        'Shared Patients (Duplicative)',
        '% of Total',
      ];

      // Rolled-up label rows first (pinned at the top, as in the UI).
      const collapsedRows = csvCollapsed.map((row) => {
        const shared = parseInt(row.shared_patients, 10) || 0;
        const providerCount = row.provider_count;
        return [
          '',
          row.label_name || 'Not Labelled',
          providerCount != null
            ? `${providerCount} provider${providerCount === 1 ? '' : 's'}`
            : '',
          '',
          '',
          shared,
          formatPct(shared, true),
        ];
      });

      const providerRows = csvRows.map((row) => {
        const provider = row.provider || {};
        const shared = parseInt(row.shared_patients, 10) || 0;
        return [
          provider.is_affiliated ? 'Yes' : 'No',
          row.provider_label?.name || 'Not Labelled',
          `${provider.last_name || ''}, ${provider.first_name || ''}`,
          provider.taxonomy
            ? SpecialtyUtil.getNameFromTaxonomy(provider.taxonomy)
            : '',
          provider.location?.organization?.name || '',
          shared,
          formatPct(shared, false),
        ];
      });

      const csvData = [headers, ...collapsedRows, ...providerRows]
        .map((row) =>
          row
            .map((field) => {
              // Escape fields that contain commas, quotes, or newlines.
              const fieldStr = String(field ?? '');
              if (
                fieldStr.includes(',') ||
                fieldStr.includes('"') ||
                fieldStr.includes('\n')
              ) {
                return `"${fieldStr.replace(/"/g, '""')}"`;
              }
              return fieldStr;
            })
            .join(','),
        )
        .join('\n');

      const filename = `doctivity_patient_flow_${tag?.name?.replace(/[^a-zA-Z0-9]/g, '_')}_${format(new Date(), 'yy_MM_dd')}.csv`;
      fileDownload(csvData, filename);
    } catch (error) {
      console.error('Error downloading patient flow:', error);
    }
  }, [
    selectedClient,
    tag?.id,
    tag?.name,
    direction,
    serviceline,
    collapse,
    rollUpTouched,
  ]);

  const result = StoreUtil.getData(flow) ?? {};
  const data = result.rows ?? [];
  const collapsed = result.collapsed ?? [];
  const totalShared = result.totalShared || 0;
  const processing = !!result.processing;
  // Rebuild progress ({ total, processed }) reported by the backend while
  // processing; null/untracked -> indeterminate spinner. Never show 100%
  // while still processing.
  const progress = result.progress || null;
  const progressPct =
    processing && progress && progress.total > 0
      ? Math.max(
          0,
          Math.min(99, Math.floor((progress.processed / progress.total) * 100)),
        )
      : null;
  // Data is retained across refetches, so while a processing poll is in
  // flight keep showing the progress view instead of flashing the loading
  // spinner every poll.
  const loading =
    (StoreUtil.isLoading(flow) || !StoreUtil.isLoaded(flow)) && !processing;

  // While the cache is rebuilding, poll so the progress bar advances and the
  // table appears as soon as processing completes.
  useEffect(() => {
    if (!processing) {
      return undefined;
    }
    const intervalId = window.setInterval(fetchFlow, 10000);
    return () => window.clearInterval(intervalId);
  }, [processing, fetchFlow]);

  // Assign each label in the group a stable color so bars/labels are colored
  // consistently across pages and filters.
  const groupLabels = result.labels ?? [];
  const colorByLabel = useMemo(() => {
    const palette = ColorUtil.getColors(Math.max(groupLabels.length, 1));
    const map = {};
    groupLabels.forEach((l, i) => {
      map[l.id] = palette[i % palette.length];
    });
    return map;
  }, [groupLabels]);
  const NEUTRAL_BAR = '#CBD5E1';

  // Accumulate every label id -> name seen this mount, so Roll Up selections
  // keep rendering as names even when a response arrives without labels
  // (e.g. transiently while the cache rebuilds, or between loads).
  const labelNamesRef = useRef(new Map());
  const labelNameById = useMemo(() => {
    const map = labelNamesRef.current;
    groupLabels.forEach((l) => map.set(l.id, l.name));
    if (tag?.id && tag?.name) {
      map.set(tag.id, tag.name);
    }
    return map;
  }, [groupLabels, tag?.id, tag?.name]);

  // What the Roll Up control actually shows/selects. Until the user touches
  // it, the default is everything rolled up (all labels + "Not Labelled");
  // the request side sends the 'all' sentinel for the same state.
  const effectiveCollapse = useMemo(
    () => (rollUpTouched ? collapse : [0, ...groupLabels.map((l) => l.id)]),
    [rollUpTouched, collapse, groupLabels],
  );

  // Clicking a rolled-up row un-rolls it (removes that label from the Roll Up).
  const onExpandCollapsed = useCallback(
    (labelId) => {
      const key = labelId == null ? 0 : labelId;
      setRollUpTouched(true);
      setCollapse(effectiveCollapse.filter((id) => id !== key));
      setOffset(0);
    },
    [effectiveCollapse],
  );

  // Clicking a provider's label name rolls that label up (adds it to Roll Up).
  const onRollUpLabel = useCallback(
    (labelId) => {
      const key = labelId == null ? 0 : labelId;
      setRollUpTouched(true);
      setCollapse(
        effectiveCollapse.indexOf(key) > -1
          ? effectiveCollapse
          : [...effectiveCollapse, key],
      );
      setOffset(0);
    },
    [effectiveCollapse],
  );

  // When every label (plus "Not Labelled") is rolled up there are no individual
  // provider rows, so hide the Specialty / Organization columns.
  const showProviderCols = !(
    groupLabels.length > 0 &&
    effectiveCollapse.length === groupLabels.length + 1
  );

  // Collapsed rows (pinned at top) are scaled among themselves; provider rows
  // among themselves -- so a big collapsed row doesn't flatten the providers.
  const { rows, maxValue, maxCollapsed } = useMemo(() => {
    let maxP = 0;
    let maxC = 0;
    const all = [...collapsed, ...data];
    const mapped = all.map((row) => {
      const total = parseInt(row.shared_patients, 10) || 0;
      if (row.type === 'collapsed') {
        maxC = Math.max(maxC, total);
      } else {
        maxP = Math.max(maxP, total);
      }
      return { ...row, total };
    });
    return { rows: mapped, maxValue: maxP || 1, maxCollapsed: maxC || 1 };
  }, [data, collapsed]);

  return (
    <Card className={classes.card}>
      <CardContent>
        <div className={classes.titleRow}>
          <Typography component='h2' variant='h6' color='primary'>
            Providers who see patients
          </Typography>
          <FormControl
            variant='outlined'
            className={classes.claimsDateDropDown}
            key='claimsdate'
          >
            <InputLabel shrink className={classes.filterLabel}>
              Claims Date
            </InputLabel>
            <Select
              value={direction}
              onChange={onDirectionChange}
              label='Claims Date'
              notched
              MenuProps={{ PaperProps: { className: classes.select } }}
            >
              <MenuItem value='after'>After</MenuItem>
              <MenuItem value='before'>Before</MenuItem>
            </Select>
          </FormControl>
          <Typography component='h2' variant='h6' color='primary'>
            Providers in{' '}
            <span className={classes.titleLabelName}>{tag?.name ?? ''}</span>
          </Typography>
          <div className={classes.titleSpacer} />
          <Tooltip title='Download'>
            <IconButton onClick={onDownload} size='large'>
              <Icon className={classes.exportIcon}>get_app</Icon>
            </IconButton>
          </Tooltip>
        </div>

        <div className={classes.filters}>
          <ServiceLineSelect
            value={serviceline}
            onChange={onServicelineChange}
            showSubServicelines
            showAll
          />
          <FormControl
            variant='outlined'
            className={classes.collapseDropDown}
            key='collapse'
          >
            <InputLabel shrink className={classes.filterLabel}>
              Roll Up
            </InputLabel>
            <Select
              multiple
              value={effectiveCollapse}
              onChange={onCollapseChange}
              label='Roll Up'
              notched
              displayEmpty
              renderValue={(selected) => {
                if (!selected || selected.length === 0) {
                  return <em>None</em>;
                }
                // Everything (all labels + "Not Labelled") selected -> "All".
                if (
                  groupLabels.length > 0 &&
                  selected.length === groupLabels.length + 1
                ) {
                  return 'All';
                }
                return selected
                  .map((id) =>
                    id === 0
                      ? 'Not Labelled'
                      : labelNameById.get(id) ?? `Label #${id}`,
                  )
                  .join(', ');
              }}
              MenuProps={{ PaperProps: { className: classes.collapseMenu } }}
            >
              {groupLabels.map((l) => (
                <MenuItem value={l.id} key={l.id}>
                  <Checkbox
                    size='small'
                    checked={effectiveCollapse.indexOf(l.id) > -1}
                  />
                  <ListItemText primary={l.name} />
                </MenuItem>
              ))}
              <MenuItem value={0} key='not-labelled'>
                <Checkbox
                  size='small'
                  checked={effectiveCollapse.indexOf(0) > -1}
                />
                <ListItemText
                  primary='Not Labelled'
                  primaryTypographyProps={{
                    className: classes.notLabelledItem,
                  }}
                />
              </MenuItem>
            </Select>
          </FormControl>
        </div>

        {loading && <LoadingView />}

        {!loading && processing && (
          <div className={classes.processing}>
            {progressPct != null ? (
              <div className={classes.progressBarWrap}>
                <LinearProgress
                  variant='determinate'
                  value={progressPct}
                  className={classes.progressBar}
                />
                <div className={classes.progressPct}>{`${progressPct}%`}</div>
              </div>
            ) : (
              <CircularProgress size={36} thickness={4} />
            )}
            <div className={classes.processingText}>
              Patient flow data is currently being processed. Results will be
              available once processing completes.
            </div>
          </div>
        )}

        {!loading && !processing && rows.length === 0 && (
          <div className={classes.empty}>
            No patient flow data for the providers in this label with the
            current filters.
          </div>
        )}

        {!loading && !processing && rows.length > 0 && (
          <div className={classes.tableContainer}>
            <div className={classes.headerRow}>
              <div className={`${classes.headerCell} ${classes.iconCol}`} />
              <div className={`${classes.headerCell} ${classes.labelCol}`}>
                Label
              </div>
              <div className={`${classes.headerCell} ${classes.nameCol}`}>
                Provider
              </div>
              {showProviderCols && (
                <div className={`${classes.headerCell} ${classes.specialtyCol}`}>
                  Specialty
                </div>
              )}
              {showProviderCols && (
                <div className={`${classes.headerCell} ${classes.orgCol}`}>
                  Organization
                </div>
              )}
              <div className={`${classes.headerCell} ${classes.sharedHeaderCol}`}>
                Shared Patients
              </div>
            </div>

            {rows.map((row, index) => {
              const barScale =
                row.type === 'collapsed' ? maxCollapsed : maxValue;
              const barWidth = row.total > 0 ? (row.total / barScale) * 100 : 0;
              // Share of the total shared patients in this view. Individual
              // provider rows show 2 decimals; rolled-up rows are coarser.
              const pct = totalShared > 0 ? (row.total / totalShared) * 100 : 0;
              let pctLabel = '';
              if (totalShared > 0) {
                pctLabel =
                  row.type === 'collapsed'
                    ? `${pct >= 10 ? pct.toFixed(0) : pct.toFixed(1)}%`
                    : `${pct.toFixed(2)}%`;
              }

              // Collapsed label-group row (no individual provider).
              if (row.type === 'collapsed') {
                const label = row.label_name || 'Not Labelled';
                const providerCount = row.provider_count;
                // The "Not Labelled" group (null label) is always grey; others
                // use their assigned color.
                const isNotLabelled = row.label_id == null;
                const barColor = isNotLabelled
                  ? NEUTRAL_BAR
                  : colorByLabel[row.label_id] || '#00a887';
                const textColor = isNotLabelled
                  ? '#64748B'
                  : colorByLabel[row.label_id];
                const colorStyle = textColor ? { color: textColor } : undefined;
                return (
                  <div
                    key={`collapsed:${row.label_id ?? 'none'}:${index}`}
                    className={`${classes.dataRow} ${classes.collapsedRow}`}
                    onClick={() => onExpandCollapsed(row.label_id)}
                    title='Click to expand'
                  >
                    <div className={`${classes.cell} ${classes.iconCol}`}>
                      <Icon
                        className={classes.collapsedIcon}
                        style={colorStyle}
                      >
                        sell
                      </Icon>
                    </div>
                    <div className={`${classes.cell} ${classes.labelCol}`}>
                      <span
                        className={classes.collapsedName}
                        style={{ ...colorStyle, textTransform: 'none' }}
                      >
                        {label}
                      </span>
                    </div>
                    <div className={`${classes.cell} ${classes.nameCol}`}>
                      <span
                        className={classes.collapsedName}
                        style={{ ...colorStyle, textTransform: 'none' }}
                      >
                        {providerCount != null
                          ? `${providerCount.toLocaleString()} provider${providerCount === 1 ? '' : 's'}`
                          : label}
                      </span>
                    </div>
                    {showProviderCols && (
                      <div className={`${classes.cell} ${classes.specialtyCol}`} />
                    )}
                    {showProviderCols && (
                      <div className={`${classes.cell} ${classes.orgCol}`} />
                    )}
                    <div className={`${classes.cell} ${classes.patientsCol}`}>
                      <Icon className={classes.patientIcon}>group</Icon>
                    </div>
                    <div className={`${classes.cell} ${classes.pctCol}`}>
                      {pctLabel}
                    </div>
                    <div className={`${classes.cell} ${classes.chartCol}`}>
                      <div className={classes.barContainer}>
                        <div
                          className={classes.bar}
                          style={{
                            width: `${barWidth}%`,
                            backgroundColor: barColor,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                );
              }

              const provider = row.provider || {};
              const orgName = provider.location?.organization?.name || '';
              const specialtyName = provider.taxonomy
                ? SpecialtyUtil.getNameFromTaxonomy(provider.taxonomy)
                : '';
              // Color by the label this provider belongs to within the group.
              const providerLabelColor = colorByLabel[row.provider_label?.id];
              const barColor = providerLabelColor || NEUTRAL_BAR;

              const oppositeLabel = direction === 'after' ? 'before' : 'after';
              const providerName = `${provider.last_name || ''}, ${provider.first_name || ''}`;
              const dialogData = {
                providerId: row.provider_id,
                providerName,
                providerLabelId: row.provider_label?.id ?? null,
                labelName: tag?.name || 'this label',
                oppositeLabel,
                storeId: `LABEL_PROVIDER_SHARED:${row.provider_id}:${direction}:${serviceline}`,
                filter: {
                  client_id: selectedClient,
                  tag_id: tag.id,
                  provider_id: row.provider_id,
                  direction,
                  serviceline,
                },
              };

              return (
                  <ButtonBase
                    key={`flow:${row.provider_id}`}
                    className={classes.dataRow}
                    onClick={() => {
                      setDialogInfo(dialogData);
                      setDialogQuery({ limit: 10, offset: 0 });
                      setDialogOpen(true);
                    }}
                  >
                  <div className={`${classes.cell} ${classes.iconCol}`}>
                    {provider.is_affiliated && (
                      <Tooltip
                        placement='bottom-start'
                        title={
                          <Typography className={classes.infoTip}>
                            Affiliated Provider
                          </Typography>
                        }
                      >
                        <Icon className={classes.affiliatedIcon}>hub</Icon>
                      </Tooltip>
                    )}
                  </div>
                  <div className={`${classes.cell} ${classes.labelCol}`}>
                    <span
                      className={`${classes.orgName} ${classes.rollUpLabelLink}`}
                      style={
                        providerLabelColor
                          ? { color: providerLabelColor, fontWeight: 600 }
                          : undefined
                      }
                      title='Click to roll up this label'
                      onClick={(e) => {
                        e.stopPropagation();
                        onRollUpLabel(row.provider_label?.id);
                      }}
                    >
                      {row.provider_label?.name || 'Not Labelled'}
                    </span>
                  </div>
                  <div className={`${classes.cell} ${classes.nameCol}`}>
                    <span className={classes.providerName}>
                      {`${provider.last_name || ''}, ${provider.first_name || ''}`}
                    </span>
                  </div>
                  {showProviderCols && (
                    <div className={`${classes.cell} ${classes.specialtyCol}`}>
                      <span className={classes.specialty}>
                        {specialtyName || '-'}
                      </span>
                    </div>
                  )}
                  {showProviderCols && (
                    <div className={`${classes.cell} ${classes.orgCol}`}>
                      <span className={classes.orgName}>{orgName || '-'}</span>
                    </div>
                  )}
                  <div className={`${classes.cell} ${classes.patientsCol}`}>
                    <Icon className={classes.patientIcon}>person</Icon>
                  </div>
                  <div className={`${classes.cell} ${classes.pctCol}`}>
                    {pctLabel}
                  </div>
                  <div className={`${classes.cell} ${classes.chartCol}`}>
                    <div className={classes.barContainer}>
                      <div
                        className={classes.bar}
                        style={{
                          width: `${barWidth}%`,
                          backgroundColor: barColor,
                        }}
                      />
                    </div>
                  </div>
                  </ButtonBase>
              );
            })}

            <Paginateable
              data={flow}
              query={{ limit, offset }}
              onQueryChange={onQueryChange}
            />
          </div>
        )}
      </CardContent>
      <ProviderSharedDialog
        classes={classes}
        open={dialogOpen}
        onClose={closeDialog}
        info={dialogInfo}
        query={dialogQuery}
        onQueryChange={setDialogQuery}
        onSuggestionApplied={onSuggestionApplied}
      />
    </Card>
  );
}

LabelPatientFlow.propTypes = {
  classes: PropTypes.object,
  tag: PropTypes.object.isRequired,
};

const styled = withStyles(styles)(LabelPatientFlow);
export { styled as LabelPatientFlow };
